import ignoredErrors from '~/lib/constants/ignored-errors'

export class SentryService {
  constructor(nuxtApp) {
    this.nuxtApp = nuxtApp
    this.config = nuxtApp.$config.public
    this.sentryIsInitialized = false
  }

  async initSentry(event) {
    event.currentTarget.removeEventListener(event.type, window.ms_initSentry) // remove the event listener that got triggered

    if (this.sentryIsInitialized) {
      return
    }

    this.sentryIsInitialized = true

    const sentryEnabled = this.config.sentryEnabled
    const sentryConfig = this.config.sentry

    if (!sentryEnabled || !sentryConfig.dsn) {
      return
    }

    await import('@sentry/vue').then((lazyLoadedSentry) => {
      // Keep the module: `init()` returns the Client, whose captureMessage
      // signature is (message, level, hint) — passing a capture context there
      // silently drops the extras and defaults the level to error.
      this.sentryModule = lazyLoadedSentry
      this.sentry = lazyLoadedSentry.init({
        app: this.nuxtApp.vueApp,
        dsn: sentryConfig.dsn,
        environment: sentryConfig.environment,
        integrations: [
          lazyLoadedSentry.browserTracingIntegration({ router: this.nuxtApp.router }),
          // Drop noise from third-party scripts (Google Tag Manager / gtag / Google
          // Ads), which throw uncaught "Maximum call stack size exceeded" errors that
          // reach window.onerror with only non-app stack frames (JAVASCRIPT-VUE-PQ /
          // -ZR). `applicationKey` in nuxt.config.ts tags our own bundle frames; an
          // event whose stack is exclusively third-party is dropped, while any error
          // containing real app frames is still reported.
          lazyLoadedSentry.thirdPartyErrorFilterIntegration({
            filterKeys: ['momentshare-fe'],
            behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
          }),
        ],
        ignoreErrors: ignoredErrors,

        tracesSampleRate: sentryConfig.tracesSampleRate, // Change in prod
        tracePropagationTargets: ['localhost', 'https://momentshare.io'],

        replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
        replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,
      })

      this.initPlugins(lazyLoadedSentry)
    })
  }

  async initPlugins(lazyLoadedSentry) {
    lazyLoadedSentry.addIntegration(
      lazyLoadedSentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [
          'https://momentshare.io',
          'https://hertzner-api.momentshare.io/api/v1',
          'https://content.dropboxapi.com',
          'https://www.googleapis.com',
        ],
        networkRequestHeaders: ['Event-Authorization', 'Authorization'],
      }),
    )
  }

  capture(error) {
    if (!this.sentry) return

    this.sentry.captureException(error)
  }

  captureMessage({ message, level = 'warning', context = {} }) {
    if (!this.sentry || !this.sentryModule) return

    try {
      // Module-level captureMessage accepts a capture context (level + extra)
      // and applies the current scope; the Client method does neither.
      this.sentryModule.captureMessage(message, { level, extra: context })
    } catch {
      // Telemetry must never break the upload flow.
    }
  }
}
